import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Shield, FileDown, Building2, CheckCircle2, AlertTriangle, Loader2, Download, RefreshCw, Lock, UserX } from 'lucide-react';
import { formatCurrency, formatDateShort } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function Conformite() {
  const [facturxDocs, setFacturxDocs] = useState([]);
  const [chorusDepots, setChorusDepots] = useState([]);
  const [pafEntries, setPafEntries] = useState([]);
  const [archives, setArchives] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState('');

  useEffect(() => {
    Promise.all([
      axios.get(`${API_URL}/facturx`).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/chorus/depots`).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/conformite/paf?limit=50`).catch(() => ({ data: [] })),
      axios.get(`${API_URL}/conformite/archives`).catch(() => ({ data: [] })),
    ]).then(([fx, ch, paf, ar]) => {
      setFacturxDocs(fx.data); setChorusDepots(ch.data); setPafEntries(paf.data); setArchives(ar.data);
    }).finally(() => setLoading(false));
  }, []);

  const verifyPAF = async () => {
    try {
      const res = await axios.get(`${API_URL}/conformite/paf/verify`);
      if (res.data.valid) toast.success(`Piste d'audit intègre — ${res.data.entries_checked} entrées vérifiées`);
      else toast.error(`Intégrité compromise à l'entrée ${res.data.broken_at_index}`);
    } catch (e) { toast.error('Erreur vérification'); }
  };

  const exportRGPD = async () => {
    try {
      const res = await axios.get(`${API_URL}/conformite/rgpd/export`);
      toast.success(`Export RGPD : ${res.data.total_records} enregistrements`);
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin" /></div>;

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold"><Shield className="w-6 h-6 inline mr-2" />Conformité</h1>

      <Tabs defaultValue="facturx">
        <TabsList className="flex-wrap">
          <TabsTrigger value="facturx">Factur-X</TabsTrigger>
          <TabsTrigger value="chorus">Chorus Pro</TabsTrigger>
          <TabsTrigger value="paf">Piste d'audit</TabsTrigger>
          <TabsTrigger value="archives">Archivage</TabsTrigger>
          <TabsTrigger value="rgpd">RGPD</TabsTrigger>
        </TabsList>

        {/* FACTUR-X */}
        <TabsContent value="facturx">
          <Card>
            <CardHeader><CardTitle><FileDown className="w-5 h-5 inline mr-2" />Factur-X</CardTitle>
              <CardDescription>Factures au format PDF/A-3 + XML EN 16931</CardDescription></CardHeader>
            <CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead>Facture</TableHead><TableHead>Profil</TableHead><TableHead>Valide</TableHead><TableHead>Date</TableHead><TableHead className="w-20">XML</TableHead></TableRow></TableHeader>
              <TableBody>
                {facturxDocs.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono text-sm">{d.facture_numero}</TableCell>
                    <TableCell><Badge variant="outline">{d.profil}</Badge></TableCell>
                    <TableCell>{d.is_valid ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <AlertTriangle className="w-4 h-4 text-red-500" />}</TableCell>
                    <TableCell className="text-sm">{formatDateShort(d.created_at)}</TableCell>
                    <TableCell><Button variant="ghost" size="sm" onClick={() => window.open(`${API_URL}/facturx/${d.id}/xml`)}><Download className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
                {facturxDocs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun document Factur-X</TableCell></TableRow>}
              </TableBody></Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* CHORUS PRO */}
        <TabsContent value="chorus">
          <Card>
            <CardHeader><CardTitle><Building2 className="w-5 h-5 inline mr-2" />Chorus Pro</CardTitle>
              <CardDescription>Dépôt et suivi des factures marchés publics</CardDescription></CardHeader>
            <CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead>Facture</TableHead><TableHead>N° Flux</TableHead><TableHead>Statut</TableHead><TableHead>Date dépôt</TableHead><TableHead className="w-20"></TableHead></TableRow></TableHeader>
              <TableBody>
                {chorusDepots.map(d => (
                  <TableRow key={d.id}>
                    <TableCell className="font-mono text-sm">{d.facture_numero}</TableCell>
                    <TableCell className="font-mono text-sm text-slate-500">{d.chorus_numero_flux}</TableCell>
                    <TableCell><Badge className={d.statut === 'acceptee' ? 'bg-green-100 text-green-700' : d.statut === 'refusee' ? 'bg-red-100 text-red-700' : 'bg-blue-100 text-blue-700'}>{d.statut}</Badge></TableCell>
                    <TableCell className="text-sm">{formatDateShort(d.deposee_at)}</TableCell>
                    <TableCell><Button variant="ghost" size="sm"><RefreshCw className="w-4 h-4" /></Button></TableCell>
                  </TableRow>
                ))}
                {chorusDepots.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-500">Aucun dépôt Chorus Pro</TableCell></TableRow>}
              </TableBody></Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* PISTE D'AUDIT */}
        <TabsContent value="paf" className="space-y-4">
          <div className="flex justify-between">
            <p className="text-sm text-slate-500">Piste d'audit fiable avec chaîne de hachage SHA-256</p>
            <Button variant="outline" onClick={verifyPAF}><CheckCircle2 className="w-4 h-4 mr-2" /> Vérifier l'intégrité</Button>
          </div>
          <Card><CardContent className="p-0">
            <Table><TableHeader><TableRow><TableHead>Horodatage</TableHead><TableHead>Type</TableHead><TableHead>Réf.</TableHead><TableHead>Action</TableHead><TableHead>Utilisateur</TableHead><TableHead>Hash</TableHead></TableRow></TableHeader>
            <TableBody>
              {pafEntries.map(e => (
                <TableRow key={e.id}>
                  <TableCell className="text-xs font-mono">{e.horodatage?.slice(0, 19)}</TableCell>
                  <TableCell><Badge variant="outline" className="text-xs">{e.document_type}</Badge></TableCell>
                  <TableCell className="text-xs">{e.document_ref || '-'}</TableCell>
                  <TableCell className="text-xs font-medium">{e.action}</TableCell>
                  <TableCell className="text-xs">{e.user_name}</TableCell>
                  <TableCell className="text-xs font-mono text-slate-400">{e.hash_integrite?.slice(0, 12)}...</TableCell>
                </TableRow>
              ))}
              {pafEntries.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Aucune entrée</TableCell></TableRow>}
            </TableBody></Table>
          </CardContent></Card>
        </TabsContent>

        {/* ARCHIVAGE */}
        <TabsContent value="archives">
          <Card>
            <CardHeader><CardTitle><Lock className="w-5 h-5 inline mr-2" />Archivage légal</CardTitle>
              <CardDescription>Documents archivés avec horodatage et hash SHA-256 — Conservation {10} ans</CardDescription></CardHeader>
            <CardContent className="p-0">
              <Table><TableHeader><TableRow><TableHead>Type</TableHead><TableHead>Réf.</TableHead><TableHead>Année</TableHead><TableHead>Archivé le</TableHead><TableHead>Rétention</TableHead><TableHead>Hash</TableHead></TableRow></TableHeader>
              <TableBody>
                {archives.map(a => (
                  <TableRow key={a.id}>
                    <TableCell><Badge variant="outline" className="text-xs">{a.document_type}</Badge></TableCell>
                    <TableCell className="text-sm">{a.document_ref || '-'}</TableCell>
                    <TableCell>{a.annee}</TableCell>
                    <TableCell className="text-sm">{formatDateShort(a.archived_at)}</TableCell>
                    <TableCell className="text-sm">{formatDateShort(a.retention_until)}</TableCell>
                    <TableCell className="text-xs font-mono text-slate-400">{a.hash_sha256?.slice(0, 12)}...</TableCell>
                  </TableRow>
                ))}
                {archives.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-500">Aucune archive</TableCell></TableRow>}
              </TableBody></Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* RGPD */}
        <TabsContent value="rgpd">
          <div className="grid gap-4 md:grid-cols-2">
            <Card>
              <CardHeader><CardTitle>Export des données</CardTitle>
                <CardDescription>Exportez toutes les données personnelles (Art. 20 RGPD)</CardDescription></CardHeader>
              <CardContent>
                <Button onClick={exportRGPD} className="bg-[#0F5156] hover:bg-[#0d464a]"><Download className="w-4 h-4 mr-2" /> Exporter mes données</Button>
              </CardContent>
            </Card>
            <Card>
              <CardHeader><CardTitle>Droit à l'oubli</CardTitle>
                <CardDescription>Anonymiser un utilisateur (Art. 17 RGPD)</CardDescription></CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3">L'anonymisation est irréversible. Les données comptables sont conservées mais les informations personnelles sont supprimées.</p>
                <Button variant="outline" className="text-red-600 border-red-200"><UserX className="w-4 h-4 mr-2" /> Gérer dans Paramètres > Utilisateurs</Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
